import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  SafeAreaView,
  ScrollView,
} from '../utils/webComponents';
import { useNavigation, useRoute } from '../utils/navigation';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from '../hooks/useTranslation';
import { useFlashcards } from '../context/FlashcardContext';
import {
  ArrowBack,
  Check,
  Close,
  FlipCameraAndroid,
} from '@mui/icons-material';
import CustomAlert from '../components/CustomAlert';

const PlayScreen = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const { setId } = route.params || {};
  const { theme } = useTheme();
  const { t } = useTranslation();
  const {
    flashcards,
    flashcardSets,
    studiedSets,
    studySession,
    startStudySession,
    endStudySession,
    updateCardProgress,
    setCurrentSet,
    moveSetToStudied,
    updateSetStats,
  } = useFlashcards();

  const [isFlipped, setIsFlipped] = useState(false);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [sessionCards, setSessionCards] = useState([]);
  const [showExitModal, setShowExitModal] = useState(false);
  const [sessionComplete, setSessionComplete] = useState(false);
  const [sessionStats, setSessionStats] = useState({ studied: 0, correct: 0 });
  const [cardRatings, setCardRatings] = useState({}); // Track quality for each card

  // Find the set
  const activeSet = flashcardSets.find(set => set.id === setId);
  const studiedSet = (studiedSets || []).find(set => set.id === setId);
  const setItem = activeSet || studiedSet;

  // Get flashcards for this set
  const setFlashcards = flashcards.filter(card => card.setId === setId);

  // Initialize study session
  useEffect(() => {
    if (setItem && setFlashcards.length > 0) {
      setCurrentSet(setItem);
      // Filter cards for this set
      setSessionCards(setFlashcards);
      setCurrentCardIndex(0);
      setIsFlipped(false);
      setSessionComplete(false);
      setSessionStats({ studied: 0, correct: 0 });
      setCardRatings({}); // Reset card ratings
      // Start the study session timer
      if (!studySession.isActive) {
        startStudySession();
      }
    }
    
    // Cleanup: end session when component unmounts
    return () => {
      if (studySession.isActive) {
        endStudySession();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setId]);

  // Handle card rating
  const handleRateCard = (quality) => {
    if (!sessionCards[currentCardIndex]) return;

    const card = sessionCards[currentCardIndex];
    const isCorrect = quality === 3; // Quality 3 = perfect/correct

    // Track rating for this card
    setCardRatings(prev => ({
      ...prev,
      [card.id]: quality
    }));

    // Update card progress
    updateCardProgress(card.id, quality);

    // Update stats
    setSessionStats(prev => ({
      studied: prev.studied + 1,
      correct: prev.correct + (isCorrect ? 1 : 0),
    }));

    // Move to next card or complete session
    if (currentCardIndex < sessionCards.length - 1) {
      setCurrentCardIndex(prev => prev + 1);
      setIsFlipped(false);
    } else {
      // Session complete - check if all cards got 100%
      // Include current card rating in the check
      const updatedRatings = { ...cardRatings, [card.id]: quality };
      const allCardsRated = sessionCards.every(c => updatedRatings[c.id] !== undefined);
      const allPerfect = allCardsRated && sessionCards.every(c => updatedRatings[c.id] === 3);
      
      // Calculate accuracy for stats
      const accuracy = sessionStats.studied > 0
        ? Math.round((sessionStats.correct / sessionStats.studied) * 100)
        : 0;
      
      // Get today's date as YYYY-MM-DD string using device's local date
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const todayString = `${year}-${month}-${day}`;
      
      // Update set stats with last studied date (when actually studied)
      if (setId) {
        updateSetStats(setId, {
          timesPlayed: 1,
          successRate: accuracy,
          lastStudied: todayString, // Use device's local date
          totalCards: setFlashcards.length,
        });
      }
      
      // If 100% and set is currently active, move to studied sets
      if (allPerfect && activeSet && setFlashcards.length > 0) {
        // Calculate next review date based on study cycle
        const studyCycle = activeSet.studyCycle || 0;
        const intervals = [1, 3, 7, 14]; // Days: 1, 3, 7, 14, then repeat 14
        const daysToAdd = studyCycle < 3 ? intervals[studyCycle] : 14;
        
        // Calculate next review date using device's local date
        const nextReviewDate = new Date(today);
        nextReviewDate.setDate(nextReviewDate.getDate() + daysToAdd);
        
        // Get local date string (YYYY-MM-DD) without timezone conversion
        const nextYear = nextReviewDate.getFullYear();
        const nextMonth = String(nextReviewDate.getMonth() + 1).padStart(2, '0');
        const nextDay = String(nextReviewDate.getDate()).padStart(2, '0');
        const nextReviewDateString = `${nextYear}-${nextMonth}-${nextDay}`;
        
        // Move set to studied sets
        moveSetToStudied(setId, nextReviewDateString, studyCycle);
      }
      
      // End the study session
      if (studySession.isActive) {
        endStudySession();
      }
      setSessionComplete(true);
    }
  };

  const handleFlip = () => {
    setIsFlipped(!isFlipped);
  };

  const handleExit = () => {
    setShowExitModal(true);
  };

  const confirmExit = async () => {
    if (studySession.isActive) {
      await endStudySession();
    }
    navigation.goBack();
  };

  const handleStudyAgain = () => {
    setSessionComplete(false);
    setCurrentCardIndex(0);
    setIsFlipped(false);
    setSessionStats({ studied: 0, correct: 0 });
    setCardRatings({}); // Reset card ratings
    // Start a new study session
    startStudySession();
  };

  const handleBackToSets = async () => {
    if (studySession.isActive) {
      await endStudySession();
    }
    navigation.goBack();
  };

  // If no set or no flashcards
  if (!setItem || setFlashcards.length === 0) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <ArrowBack sx={{ fontSize: 24, color: theme.text }} />
          </TouchableOpacity>
        </View>
        <View style={styles.emptyContainer}>
          <Text style={[styles.emptyText, { color: theme.text }]}>
            {t('flashcard.noCardsToStudy')}
          </Text>
          <Text style={[styles.emptySubtext, { color: theme.textSecondary }]}>
            {t('flashcard.noCardsToStudySubtitle')}
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  // Session complete screen
  if (sessionComplete) {
    const accuracy = sessionStats.studied > 0
      ? Math.round((sessionStats.correct / sessionStats.studied) * 100)
      : 0;

    return (
      <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]}>
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.completeContent}
        >
          <View style={styles.completeContainer}>
            <View style={[styles.completeIcon, { backgroundColor: theme.primaryLight }]}>
              <Check sx={{ fontSize: 64, color: theme.primary }} />
            </View>
            <Text style={[styles.completeTitle, { color: theme.text }]}>
              {t('flashcard.sessionComplete')}
            </Text>
            <Text style={[styles.completeSubtitle, { color: theme.textSecondary }]}>
              {t('flashcard.greatJobStudying')}
            </Text>

            <View style={[styles.statsCard, { backgroundColor: theme.surface, borderColor: theme.border }]}>
              <View style={styles.statRow}>
                <Text style={[styles.statLabel, { color: theme.textSecondary }]}>
                  {t('flashcard.cardsStudied')}
                </Text>
                <Text style={[styles.statValue, { color: theme.text }]}>
                  {sessionStats.studied}
                </Text>
              </View>
              <View style={[styles.statDivider, { backgroundColor: theme.border }]} />
              <View style={styles.statRow}>
                <Text style={[styles.statLabel, { color: theme.textSecondary }]}>
                  {t('flashcard.accuracy')}
                </Text>
                <Text style={[styles.statValue, { color: theme.text }]}>
                  {accuracy}%
                </Text>
              </View>
            </View>

            <View style={styles.completeButtons}>
              <TouchableOpacity
                style={[styles.completeButton, styles.studyAgainButton, { backgroundColor: theme.primary }]}
                onPress={handleStudyAgain}
              >
                <Text style={[styles.completeButtonText, { color: 'white' }]}>
                  {t('study.continueStudying')}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.completeButton, styles.backButton, { backgroundColor: theme.surface, borderColor: theme.border }]}
                onPress={handleBackToSets}
              >
                <Text style={[styles.completeButtonText, { color: theme.text }]}>
                  {t('study.backToSets')}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </ScrollView>
      </SafeAreaView>
    );
  }

  // Main study screen
  const currentCard = sessionCards[currentCardIndex];
  const progress = ((currentCardIndex + 1) / sessionCards.length) * 100;

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={handleExit}
        >
          <ArrowBack sx={{ fontSize: 24, color: theme.text }} />
        </TouchableOpacity>
        <View style={styles.headerContent}>
          <Text style={[styles.setName, { color: theme.text }]}>
            {setItem.name.toUpperCase()}
          </Text>
          <Text style={[styles.progressText, { color: theme.textSecondary }]}>
            {currentCardIndex + 1} / {sessionCards.length}
          </Text>
        </View>
      </View>

      {/* Progress Bar */}
      <View style={[styles.progressBarContainer, { backgroundColor: theme.border }]}>
        <View
          style={[styles.progressBar, { backgroundColor: theme.primary, width: `${progress}%` }]}
        />
      </View>

      {/* Card */}
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.cardContainer}
      >
        <View style={styles.cardWrapper}>
          <TouchableOpacity
            style={[
              styles.card,
              { 
                backgroundColor: theme.surface, 
                borderColor: theme.border,
                transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
              },
            ]}
            onPress={handleFlip}
          >
            {/* Front Side */}
            <View style={[styles.cardFace, styles.cardFront, { backgroundColor: theme.surface }]}>
              <View style={styles.cardTop}>
                <Text style={[styles.cardLabel, { color: theme.textMuted }]}>
                  {t('flashcard.question')}
                </Text>
              </View>
              <View style={styles.cardContent}>
                <Text style={[styles.cardText, { color: theme.text }]}>
                  {currentCard?.front}
                </Text>
              </View>
              <View style={[styles.flipHint, { borderTopColor: theme.border }]}>
                <FlipCameraAndroid sx={{ fontSize: 20, color: theme.textMuted }} />
                <Text style={[styles.flipHintText, { color: theme.textMuted }]}>
                  {t('study.flipCard')}
                </Text>
              </View>
            </View>
            
            {/* Back Side */}
            <View style={[styles.cardFace, styles.cardBack, { backgroundColor: theme.surface }]}>
              <View style={styles.cardTop}>
                <Text style={[styles.cardLabel, { color: theme.textMuted }]}>
                  {t('flashcard.answer')}
                </Text>
              </View>
              <View style={styles.cardContent}>
                <Text style={[styles.cardText, { color: theme.text }]}>
                  {currentCard?.back}
                </Text>
              </View>
            </View>
          </TouchableOpacity>
        </View>
      </ScrollView>

      {/* Rating Buttons - Only show when flipped */}
      {isFlipped && (
        <View style={styles.ratingContainer}>
          <TouchableOpacity
            style={[styles.ratingButton, styles.wrongButton, { backgroundColor: theme.error }]}
            onPress={() => handleRateCard(0)}
          >
            <Close sx={{ fontSize: 24, color: 'white' }} />
            <Text style={[styles.ratingButtonText, { color: 'white' }]}>
              {t('flashcard.wrong')}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.ratingButton, styles.correctButton, { backgroundColor: theme.success || '#50E3C2' }]}
            onPress={() => handleRateCard(3)}
          >
            <Check sx={{ fontSize: 24, color: 'white' }} />
            <Text style={[styles.ratingButtonText, { color: 'white' }]}>
              {t('flashcard.correct')}
            </Text>
          </TouchableOpacity>
        </View>
      )}

      {/* Exit Confirmation Modal */}
      <CustomAlert
        visible={showExitModal}
        title={t('study.exitSessionTitle')}
        message={t('study.exitSessionMessage')}
        buttons={[
          { text: t('study.exitSessionCancel'), onPress: () => setShowExitModal(false), style: 'cancel' },
          { text: t('study.exitSessionConfirm'), onPress: confirmExit, style: 'destructive' },
        ]}
        onClose={() => setShowExitModal(false)}
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 16,
  },
  backButton: {
    padding: 8,
    marginRight: 12,
  },
  headerContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  setName: {
    fontSize: 18,
    fontWeight: '700',
    letterSpacing: 0.5,
  },
  progressText: {
    fontSize: 14,
    marginTop: 4,
  },
  progressBarContainer: {
    height: 4,
    width: '100%',
    borderRadius: 2,
    overflow: 'hidden',
  },
  progressBar: {
    height: '100%',
    borderRadius: 2,
    transition: 'width 0.3s ease',
  },
  scrollView: {
    flex: 1,
  },
  cardContainer: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  cardWrapper: {
    width: '100%',
    maxWidth: 350,
    height: 450,
    perspective: '1000px',
    WebkitPerspective: '1000px',
  },
  card: {
    width: '100%',
    height: '100%',
    borderRadius: 16,
    borderWidth: 1,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    cursor: 'pointer',
    position: 'relative',
    transformStyle: 'preserve-3d',
    WebkitTransformStyle: 'preserve-3d',
    transition: 'transform 0.6s',
    WebkitTransition: 'transform 0.6s',
  },
  cardFace: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    borderRadius: 16,
    padding: 24,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    backfaceVisibility: 'hidden',
    WebkitBackfaceVisibility: 'hidden',
    borderWidth: 1,
    boxSizing: 'border-box',
  },
  cardTop: {
    width: '100%',
    flexShrink: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  cardLabel: {
    fontSize: 14,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    textAlign: 'center',
    display: 'block',
  },
  cardFront: {
    transform: 'rotateY(0deg)',
    WebkitTransform: 'rotateY(0deg)',
  },
  cardBack: {
    transform: 'rotateY(180deg)',
    WebkitTransform: 'rotateY(180deg)',
  },
  cardContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    minHeight: 0,
    paddingVertical: 20,
  },
  cardText: {
    fontSize: 20,
    lineHeight: 1.5,
    textAlign: 'center',
    width: '100%',
  },
  flipHint: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingTop: 16,
    borderTopWidth: 1,
    flexShrink: 0,
  },
  flipHintText: {
    fontSize: 14,
  },
  ratingContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: 12,
    padding: 16,
    paddingBottom: 24,
  },
  ratingButton: {
    flex: 1,
    borderRadius: 12,
    padding: 16,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  wrongButton: {
    // Error color from theme
  },
  correctButton: {
    // Success color from theme
  },
  ratingButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  emptyContainer: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  emptyText: {
    fontSize: 20,
    fontWeight: '600',
    marginBottom: 8,
    textAlign: 'center',
  },
  emptySubtext: {
    fontSize: 14,
    textAlign: 'center',
  },
  completeContent: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  completeContainer: {
    width: '100%',
    maxWidth: 500,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  completeIcon: {
    width: 120,
    height: 120,
    borderRadius: 60,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 24,
  },
  completeTitle: {
    fontSize: 28,
    fontWeight: '700',
    marginBottom: 8,
    textAlign: 'center',
  },
  completeSubtitle: {
    fontSize: 16,
    marginBottom: 32,
    textAlign: 'center',
  },
  statsCard: {
    width: '100%',
    borderRadius: 12,
    padding: 24,
    borderWidth: 1,
    marginBottom: 32,
  },
  statRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
  },
  statLabel: {
    fontSize: 16,
  },
  statValue: {
    fontSize: 24,
    fontWeight: '700',
  },
  statDivider: {
    height: 1,
    width: '100%',
    marginVertical: 8,
  },
  completeButtons: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  completeButton: {
    width: '100%',
    borderRadius: 12,
    padding: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
  },
  studyAgainButton: {
    borderWidth: 0,
  },
  completeButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
});

export default PlayScreen;
